import React, { useState } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Close from '@mui/icons-material/Close';
import Popover from '@mui/material/Popover';
import { PASSENGER_STALE_MS } from './remoteContract';

function ProfileAvatar({ imageUrl, name, decorative = false }) {
  const [failed, setFailed] = useState(false);
  const label = `${name}'s Spotify profile picture`;
  if (imageUrl && !failed) {
    return <img className="spotify-account-avatar" src={imageUrl} alt={decorative ? '' : label} onError={() => setFailed(true)} />;
  }
  const initials = name.split(/\s+/).slice(0, 2).map(part => Array.from(part)[0]).join('').toUpperCase();
  return <span className="spotify-account-avatar" role={decorative ? undefined : 'img'} aria-label={decorative ? undefined : label}>{initials}</span>;
}

// Anonymous display labels only. Membership comes from the driver's live map.
export function rideMembers(live = {}, self, now = Date.now()) {
  const ids = Object.keys(live).sort().filter(id => {
    const member = live[id] || {};
    const updated = member.pax_update_time?.toMillis?.();
    if ('expired' in member || (updated && now - updated > PASSENGER_STALE_MS)) return false;
    return member.proximity === true || member.driver_secret === true;
  });
  if (self && !ids.includes(self)) ids.unshift(self);
  let guest = 0;
  return ids.map(id => ({ id, label: id === self ? 'You' : `Passenger ${++guest}` }));
}

export function passengerLabel(live, self, id) {
  return rideMembers(live, self).find(member => member.id === id)?.label || 'Passenger';
}

export default function Passengers({ live, self, driverName, account, onLogout }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const expanded = Boolean(anchorEl);
  const members = rideMembers(live, self);
  const others = members.filter(member => member.id !== self);
  const ordered = [...members.filter(member => member.id === self), ...others];
  const showSelf = Boolean(self || account);
  // The verified account remains usable while the passenger ID is loading.
  const avatars = [...(showSelf ? [{ self: true }] : []), ...others];
  const summary = showSelf ? (others.length ? `You + ${others.length}` : 'Just you') : `${members.length} passengers`;
  const triggerLabel = `${account ? `Fellow listeners and Spotify account for ${account.name}. ` : ''}${summary} in this ride`;
  const close = () => setAnchorEl(null);
  const logout = () => { close(); onLogout(); };
  return (
    <section className="ride-presence" aria-label="Passengers in this ride">
      <button type="button" className="passenger-summary" onClick={event => setAnchorEl(event.currentTarget)} aria-label={triggerLabel} title={triggerLabel} aria-haspopup="dialog" aria-expanded={expanded} aria-controls={expanded ? 'ride-members' : undefined}>
        <span className="avatar-stack" aria-hidden="true">
          {avatars.slice(0, 3).map((member, i) => <span key={member.self ? 'self' : `passenger-${member.id}`} className={`passenger-avatar avatar-${i}${member.self && account ? ' passenger-avatar-self' : ''}`}>
            {member.self ? (account ? <ProfileAvatar key={account.imageUrl} {...account} decorative /> : 'Y') : member.label.replace('Passenger ', '')}
          </span>)}
          {avatars.length > 3 && <span className="passenger-avatar avatar-overflow">+{avatars.length - 3}</span>}
        </span>
        <ExpandMore className={expanded ? 'chevron expanded-chevron' : 'chevron'} />
      </button>
      <Popover open={expanded} anchorEl={anchorEl} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} marginThreshold={16} PaperProps={{ className: 'ride-members', id: 'ride-members', role: 'dialog', 'aria-modal': true, 'aria-label': 'Fellow listeners', tabIndex: -1 }}>
        <div className="ride-members-heading"><h2>Fellow listeners</h2><button type="button" className="ride-members-close" aria-label="Close passenger list" onClick={close}><Close /></button></div>
        {account && <div className="listener-account">
          <ProfileAvatar key={account.imageUrl} {...account} />
          <div className="listener-account-details"><span className="listener-account-name">{account.name}</span><span className="listener-account-status">You · Spotify</span></div>
          <button type="button" className="listener-logout" onClick={logout} aria-label={`Log out of Spotify for ${account.name}`}>Log out</button>
        </div>}
        {driverName && <p>Listening with {driverName}</p>}
        <div className="ride-members-list">{(account ? others : ordered).map(member => <span className="member-chip" key={member.id}><span className="live-dot" />{member.label}</span>)}</div>
        <p>Everyone shares the queue.</p>
      </Popover>
    </section>
  );
}
